<template>
  <div class="columns-container">
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">
            {{ i18n.t('/change-order/configuration/options') }}
          </div>
        </template>
        <template #body>
          <configuration-feature
            v-for="feature in originalOptions"
            :key="feature.Id"
            :can-detect-feature-change="true"
            :original-features="editableOptions"
            :feature="feature"
          />
        </template>
      </quote-list-block>
    </div>
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">
            {{ i18n.t('/change-order/configuration/options') }}
          </div>
          <div v-if="canChangeOption.Allowed" class="edit-dialog" @click="showChangeOptionsDialog">
            <i class="fa-solid fa-pencil"></i>
          </div>
          <div
            v-if="!canChangeOption.Allowed && canChangeOption.Reason !== ''"
            class="edit-dialog"
            :title="canChangeOption.Reason"
          >
            <i class="fa-solid fa-lock"></i>
          </div>
        </template>
        <template #body>
          <configuration-feature
            v-for="feature in editableOptions"
            :key="feature.Id"
            :can-detect-feature-change="true"
            :original-features="originalOptions"
            :feature="feature"
          />
        </template>
      </quote-list-block>
    </div>
    <multiple-feature-change-dialog
      v-if="showOptionsChangeDialog"
      :title="i18n.t('/change-order/configuration/change-options')"
      :available-items="availableOptions"
      :selected-items="changedOptions"
      :show-options-search-input="true"
      @close="showOptionsChangeDialog = false"
      @confirm="confirmOptionsChange"
    />
  </div>
</template>

<script>
import QuoteListBlock from '@/components/generic/presentational/molecules/quoteListBlock/QuoteListBlock.vue'
import ConfigurationFeature from '@/components/changeOrder/presentational/organisms/configurationFeature/ConfigurationFeature.vue'
import MultipleFeatureChangeDialog from '@/components/changeOrder/presentational/organisms/multipleFeatureChangeDialog/multipleFeatureChangeDialog.vue'
import { mapGetters } from 'vuex'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    MultipleFeatureChangeDialog,
    ConfigurationFeature,
    QuoteListBlock,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showOptionsChangeDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    originalOptions() {
      return this.optionsAndAccessoriesWithoutInterior(
        this.changeOrder.CarConfiguration.OriginalOptions
      )
    },
    editableOptions() {
      return this.optionsAndAccessoriesWithoutInterior(
        this.changeOrder.CarConfiguration.ChangedOptions
      )
    },
    changedOptions() {
      return this.optionsAndAccessoriesWithoutInterior(
        this.changeOrder.CarConfiguration.ChangedOptions
      )
    },
    availableOptions() {
      let availableOptions = this.changeOrder.CarConfiguration.AvailableOptions
      let filteredOptions = availableOptions.filter((option) => this.isGeneralOption(option))

      return this.sortOptionsBy(filteredOptions, 'shortId')
    },
    canChangeOption() {
      if (
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
      ) {
        return {
          Allowed: false,
          Reason: 'Order has been submitted or deleted',
        }
      }

      if (this.changeOrder.CarConfiguration.HasUnknownOkapiModel) {
        return {
          Allowed: false,
          Reason:
            'Het is voor dit voertuig niet mogelijk om configuratiewijzigingen door te voeren. ' +
            'Neem contact op met DSS',
        }
      }

      return this.changeOrder.Permissions.CanChangeFactoryOption
    },
  },
  methods: {
    optionsAndAccessoriesWithoutInterior(options) {
      return options?.filter((option) => this.isGeneralOption(option)) || []
    },
    isGeneralOption(option) {
      // Mixed packages belong in the options list regardless of their type
      const hasOptionType = option.Type === 'GENERAL_OPTION' || option.MixedPackage === true
      if (this.dealerSettings.isImporter) {
        return (
          (hasOptionType || option.Type === 'HIDDEN_OPTION') &&
          (option.Classification === 'OPTIONAL_EQUIPMENT' ||
            option.Classification === 'LOCAL_EQUIPMENT')
        )
      }
      return hasOptionType && option.Classification === 'OPTIONAL_EQUIPMENT'
    },
    showChangeOptionsDialog() {
      this.showOptionsChangeDialog = true
    },
    confirmOptionsChange(changedOptions) {
      let otherOptions = this.changeOrder.CarConfiguration.ChangedOptions.filter(
        (option) => !this.isGeneralOption(option)
      )

      // A mixed package can only be on the car once: when it is already selected
      // as rim it stays the rim and is not added as option again
      let selectedRimIds = otherOptions
        .filter((option) => option.Type === 'EXTERIOR_RIMS')
        .map((option) => option.Id)
      let conflictingOptions = changedOptions.filter((option) => selectedRimIds.includes(option.Id))
      let addableOptions = changedOptions.filter((option) => !selectedRimIds.includes(option.Id))

      conflictingOptions.forEach((option) =>
        EventBus.$emit(
          'notification-warning',
          `Optie ${option.ShortId} is al geselecteerd als velg. Deze optie is daarom niet nogmaals toegevoegd.`
        )
      )

      // Selected as normal option: store (and send to PCCM) as GENERAL_OPTION
      // with MixedPackage false, matching how PCCM delivers it back
      let normalizedOptions = addableOptions.map((option) =>
        option.MixedPackage ? { ...option, Type: 'GENERAL_OPTION', MixedPackage: false } : option
      )

      this.changeOrder.CarConfiguration.ChangedOptions = otherOptions.concat(normalizedOptions)

      this.$store.commit('changeOrder/setCarConfigurationStatus')
      this.showOptionsChangeDialog = false
    },
    sortOptionsBy(options, key) {
      return options.sort((a, b) => {
        let valA = a[key]
        let valB = b[key]
        if (valA < valB) {
          return -1
        }
        if (valA > valB) {
          return 1
        }
        return 0
      })
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.row) {
  grid-template-columns: auto 4em 8em 1em 2em;
  grid-template-areas: 'title code price-incl info-icon edit-dialog';
}

.info-icon {
  cursor: pointer;
}

.edit-dialog {
  cursor: pointer;
}
.columns-container {
  display: flex;
  justify-content: space-between; /* Distribute columns evenly */
}

.column {
  flex: 1;
  margin: 0 10px;
}
</style>
